<div class="flex flex-col gap-4">

  @for (group of groups; track group.titleKey) {
    <div class="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden">

      <!-- Group header -->
      <div class="px-5 py-3 border-b border-gray-100 dark:border-dark-border">
        <span class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
          {{ group.titleKey | translate }}
        </span>
      </div>

      <!-- Status strips -->
      <div class="divide-y divide-gray-50 dark:divide-dark-border">
        @for (row of group.rows; track row.labelKey) {
          <div class="flex gap-5 items-start px-5 py-4">

            <!-- Label -->
            <div class="w-20 shrink-0 pt-px text-xs text-gray-400 dark:text-gray-500 leading-5">
              {{ row.labelKey | translate }}
            </div>

            <!-- Bar + axis + stats -->
            <div class="flex-1 flex flex-col gap-1.5 min-w-0">

              @if (row.loading) {
                <div class="h-7 w-full rounded-sm bg-gray-100 dark:bg-dark-border animate-pulse"></div>
                <div class="h-3 w-1/3 rounded bg-gray-50 dark:bg-dark-border animate-pulse mt-1"></div>

              } @else if (row.error) {
                <div class="h-7 rounded-sm bg-red-50 dark:bg-red-900/10 flex items-center px-3 text-xs text-red-400">
                  {{ 'dashboard.datahub.communication.loadError' | translate }}
                </div>

              } @else {
                <!-- Segmented bar -->
                <div class="flex gap-px h-7 rounded-sm overflow-hidden">
                  @for (seg of row.segments; track $index) {
                    <div
                      class="flex-1 transition-opacity duration-75 hover:opacity-70 cursor-default"
                      [class]="segmentClass(seg)"
                      (mouseenter)="onSegmentEnter($event, seg)"
                      (mouseleave)="onSegmentLeave()"
                    ></div>
                  }
                </div>

                <!-- Time axis -->
                @if (row.segments.length) {
                  <div class="flex justify-between text-[10px] text-gray-300 dark:text-gray-600 px-px">
                    <span>{{ fmtHour(row.segments[0].startUtc) }}</span>
                    <span>{{ fmtHour(row.segments[row.segments.length - 1].endUtc) }}</span>
                  </div>
                }

                <!-- Stats line -->
                <div class="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  <span>{{ total(row) }} {{ 'dashboard.datahub.communication.total' | translate | lowercase }}</span>
                  <span class="text-gray-200 dark:text-gray-700">·</span>
                  <span [class.text-red-400]="failures(row) > 0">
                    {{ failures(row) }} {{ 'dashboard.datahub.communication.failures' | translate | lowercase }}
                  </span>
                  <span class="ml-auto text-gray-300 dark:text-gray-600">
                    {{ 'dashboard.datahub.communication.lastSeen' | translate }}: {{ lastSeen(row) }}
                  </span>
                </div>
              }

            </div>
          </div>
        }
      </div>

      <!-- Volume chart -->
      @if (group.volumeOptions) {
        <div class="border-t border-gray-100 dark:border-dark-border px-5 pt-4 pb-5">
          <div class="text-[10px] font-semibold text-gray-300 dark:text-gray-600 uppercase tracking-widest mb-2">
            {{ 'dashboard.datahub.communication.total' | translate }}
          </div>
          <div class="relative h-48">
            <div class="absolute inset-0">
              <app-echart [options]="group.volumeOptions" height="100%"></app-echart>
            </div>
          </div>
        </div>
      } @else {
        <div class="border-t border-gray-100 dark:border-dark-border px-5 py-4">
          <div class="h-48 rounded-sm bg-gray-50 dark:bg-dark-border animate-pulse"></div>
        </div>
      }

    </div>
  }

</div>

<!-- Floating tooltip -->
@if (tooltip) {
  <div
    class="fixed z-50 pointer-events-none bg-gray-900 text-white text-xs rounded-md px-3 py-2 shadow-xl"
    [style.left.px]="tooltip.x"
    [style.top.px]="tooltip.y - 10"
    style="transform: translateX(-50%) translateY(-100%)"
  >
    <div class="text-gray-400 mb-1.5 tabular-nums">
      {{ fmtHour(tooltip.segment.startUtc) }} – {{ fmtHour(tooltip.segment.endUtc) }}
    </div>
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
        <span class="tabular-nums">{{ tooltip.segment.successCount }}</span>
        <span class="text-gray-400">{{ 'dashboard.datahub.communication.success' | translate | lowercase }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"></span>
        <span class="tabular-nums">{{ tooltip.segment.failureCount }}</span>
        <span class="text-gray-400">{{ 'dashboard.datahub.communication.failures' | translate | lowercase }}</span>
      </div>
    </div>
  </div>
}
